import React, { useState, useEffect } from "react";
import {
    Box,
    Toolbar,
    Button,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Typography,
    IconButton,
    CircularProgress,
    Snackbar,
    Tooltip
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../api/useApi";
import UpdateProfileDialog from "./UpdateBuyerProfileDialog";
import { getListingImageUrl } from '../utils/imageUtils';
import LogoutIcon from "@mui/icons-material/Logout";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ListingsPage = () => {
    const navigate = useNavigate();
    const { user, setUser, logout } = useAuth();
    const { authFetch } = useApi();

    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [profileOpen, setProfileOpen] = useState(false);
    const [listings, setListings] = useState([]);

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    useEffect(() => {
        const fetchListings = async () => {
            setLoading(true);
            try {
                const res = await authFetch(`/listings`, {
                    headers: {
                        Accept: "application/json"
                    }
                });
                if (!res.ok) throw new Error("Failed to load listings");
                const data = await res.json();
                const items = data.map(item => ({
                    id: item.id,
                    title: item.listingTitle,
                    description: item.description,
                    image: item.car.images?.[0]?.path,
                    car: item.car,
                    price: item.price
                }));
                setListings(items);
            } catch (err) {
                console.error(err);
                showSnackbar("Could not fetch listings", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchListings();
    }, [authFetch]);

    const showSnackbar = (msg, sev = 'success') => setSnackbar({ open: true, message: msg, severity: sev });
    const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

    const handleProfileOpen = () => setProfileOpen(true);
    const handleProfileClose = () => setProfileOpen(false);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                <Toolbar sx={{ justifyContent: 'flex-end', borderBottom: '1px solid', borderColor: 'divider' }}>
                    {/* Centered Logo */}
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/logo.png`}
                        alt="Logo"
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            height: 36
                        }}
                    />
                    {user.userType === 'BUYER' ?
                        <React.Fragment>
                            <Tooltip title="Profile">
                                <IconButton onClick={handleProfileOpen}>
                                    <AccountCircleIcon fontSize="large" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Logout">
                                <IconButton onClick={handleLogout}>
                                    <LogoutIcon fontSize="large" />
                                </IconButton>
                            </Tooltip>
                        </React.Fragment>
                        : <Tooltip title="Back to admin listings">
                            <IconButton onClick={() => navigate("/admin/listings")}>
                                <ArrowBackIcon />
                            </IconButton>
                        </Tooltip>}
                </Toolbar>

                {/* Profile Dialog */}
                <UpdateProfileDialog
                    open={profileOpen}
                    onClose={handleProfileClose}
                    user={user}
                    onUpdated={newUser => setUser(newUser)}
                />

                {/* Listings Grid */}
                <Box p={2}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" mt={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {listings.map(list => (
                                <Grid key={list.id} item xs={12} sm={6} md={4}>
                                    <Card
                                        onClick={() => navigate(`/buyer/listings/${list.id}`)}
                                        sx={{
                                            borderRadius: 2,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            transition: 'transform 0.3s, box-shadow 0.3s',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                                            }
                                        }}
                                    >
                                        <CardMedia
                                            component="img"
                                            height="210"
                                            image={getListingImageUrl(list.image, 'thumbnail')}
                                            loading="lazy"
                                            alt={list.title}
                                        />
                                        <CardContent>
                                            {/* Title */}
                                            <Typography variant="h6" gutterBottom>
                                                {list.title}
                                            </Typography>
                                            {/* Car details vertical */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Variant: {list.car.variant || '-'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Fuel: {list.car.displayFuelType}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    First reg.: {new Date(list.car.firstRegistrationDate).toLocaleDateString()}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Price: {list.price}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                        <Box sx={{ px: 2, pb: 2 }}>
                                            <Button size="small" variant="contained" onClick={() => navigate(`/buyer/listings/${list.id}`)}>View More</Button>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </MuiAlert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
};

export default ListingsPage;
